import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, Plus, Trash2, XCircle } from 'lucide-react';
import { fadeInUp, staggerFast } from '../../lib/animations';
import useLocalStorage from '../../lib/useLocalStorage';

// ─── Tipos & constantes ───────────────────────────────────────────────────────

type Gravidade = 'leve' | 'media' | 'grave' | 'gravissima';

interface Infracao {
  id: string;
  data: string; // YYYY-MM-DD
  gravidade: Gravidade;
}

interface CnhState {
  ear: boolean;       // Exerce Atividade Remunerada
  infracoes: Infracao[];
}

const INITIAL: CnhState = { ear: false, infracoes: [] };

const PONTOS: Record<Gravidade, number> = {
  leve: 3,
  media: 4,
  grave: 5,
  gravissima: 7,
};

const GRAVIDADE_LABEL: Record<Gravidade, string> = {
  leve: 'Leve — 3 pontos',
  media: 'Média — 4 pontos',
  grave: 'Grave — 5 pontos',
  gravissima: 'Gravíssima — 7 pontos',
};

const GRAVIDADE_STYLE: Record<Gravidade, string> = {
  leve: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  media: 'border-yellow-400/40 bg-yellow-400/10 text-yellow-300',
  grave: 'border-orange-400/40 bg-orange-400/10 text-orange-300',
  gravissima: 'border-red-400/40 bg-red-400/10 text-red-300',
};

const GRAVIDADE_BADGE: Record<Gravidade, string> = {
  leve: 'bg-emerald-400/20 text-emerald-300',
  media: 'bg-yellow-400/20 text-yellow-300',
  grave: 'bg-orange-400/20 text-orange-300',
  gravissima: 'bg-red-400/20 text-red-300',
};

/**
 * Calculates the points limit based on EAR.
 *
 * Rules (CTB):
 *  - EAR (atividade remunerada): limite 40 pts
 *  - Condutor comum: limite 20 pts
 */
function calcLimite(ear: boolean, _gravissimas: number): number {
  return ear ? 40 : 20;
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function fmtBR(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

// ─── Componente ───────────────────────────────────────────────────────────────

interface Props {
  className?: string;
}

/**
 * Simulador de pontos da CNH com janela deslizante de 12 meses.
 * Estado (EAR + lista de infrações) persistido no localStorage (chave "gas2:cnh").
 */
const SimuladorCNH: React.FC<Props> = ({ className = '' }) => {
  const [state, setState, clearState] = useLocalStorage<CnhState>('cnh', INITIAL);
  const [newData, setNewData] = useState(todayStr());
  const [newGravidade, setNewGravidade] = useState<Gravidade>('leve');

  // Janela de 12 meses a partir de hoje
  const cutoff = useMemo(
    () => new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
    [],
  );

  const infracoesAtivas = useMemo(
    () => state.infracoes.filter(i => new Date(i.data + 'T00:00:00') >= cutoff),
    [state.infracoes, cutoff],
  );

  const totalPontos = infracoesAtivas.reduce((s, i) => s + PONTOS[i.gravidade], 0);
  const gravissimas = infracoesAtivas.filter(i => i.gravidade === 'gravissima').length;
  const limite = calcLimite(state.ear, gravissimas);
  const pct = Math.min((totalPontos / limite) * 100, 100);

  type Status = 'safe' | 'warning' | 'danger';
  const status: Status =
    totalPontos >= limite ? 'danger' : totalPontos >= limite * 0.7 ? 'warning' : 'safe';

  const statusCfg: Record<Status, { Icon: React.FC<{ className?: string; strokeWidth?: number }>; color: string; bar: string; label: string }> = {
    safe: { Icon: CheckCircle, color: 'text-emerald-400', bar: 'bg-emerald-400', label: 'Habilitação segura' },
    warning: { Icon: AlertTriangle, color: 'text-amber-400', bar: 'bg-amber-400', label: 'Atenção — pontuação elevada' },
    danger: { Icon: XCircle, color: 'text-red-400', bar: 'bg-red-500', label: 'Risco de suspensão!' },
  };

  const cfg = statusCfg[status];
  const StatusIcon = cfg.Icon;

  const addInfracao = () => {
    if (!newData) return;
    const entry: Infracao = {
      id: `${Date.now()}-${Math.random()}`,
      data: newData,
      gravidade: newGravidade,
    };
    setState(prev => ({ ...prev, infracoes: [...prev.infracoes, entry] }));
  };

  const removeInfracao = (id: string) =>
    setState(prev => ({ ...prev, infracoes: prev.infracoes.filter(i => i.id !== id) }));

  const toggleEar = () => setState(prev => ({ ...prev, ear: !prev.ear }));

  // infrações ordenadas mais recentes primeiro para exibição
  const infracoesOrdenadas = useMemo(
    () => [...state.infracoes].sort((a, b) => b.data.localeCompare(a.data)),
    [state.infracoes],
  );

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={`rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-6 ${className}`}
    >
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-white">
          Simulador de Pontos CNH
        </h3>
        <button
          onClick={clearState}
          title="Limpar todos os dados salvos"
          className="text-slate-500 hover:text-red-400 transition-colors"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.8} />
        </button>
      </div>

      {/* Toggle EAR */}
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <div
          role="switch"
          aria-checked={state.ear}
          onClick={toggleEar}
          className={`mt-0.5 relative flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
            state.ear ? 'bg-amber-400' : 'bg-slate-700'
          }`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
              state.ear ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </div>
        <span className="text-sm font-medium text-slate-300 leading-relaxed">
          Exerço Atividade Remunerada com a CNH (EAR)
          <span className="block text-xs text-slate-500 font-normal mt-0.5">
            Taxista, motorista de app, transporte escolar, etc. — limite ampliado para 40 pts.
          </span>
        </span>
      </label>

      {/* Painel de pontuação */}
      <div className="rounded-2xl bg-slate-800 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className={`h-5 w-5 ${cfg.color}`} strokeWidth={2} />
            <span className={`font-semibold text-sm ${cfg.color}`}>{cfg.label}</span>
          </div>
          <span className="text-white font-bold text-xl">
            {totalPontos}
            <span className="text-slate-400 font-normal text-sm"> / {limite} pts</span>
          </span>
        </div>

        {/* Barra de progresso */}
        <div className="h-3 w-full rounded-full bg-slate-700 overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${cfg.bar}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>0</span>
          <span>Limite: {limite} pontos</span>
        </div>

        {/* Avisos contextuais */}
        {state.ear && (
          <p className="text-xs text-amber-300 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2">
            ✅ EAR ativado: limite de 40 pontos (condutor profissional remunerado).
          </p>
        )}
        {!state.ear && (
          <p className="text-xs text-slate-400 bg-slate-700/40 border border-slate-600 rounded-lg px-3 py-2">
            Condutor comum: limite de 20 pontos. Ative EAR se usar a CNH profissionalmente.
          </p>
        )}
      </div>

      {/* Formulário para adicionar infração */}
      <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-4 space-y-3">
        <p className="text-sm font-semibold text-slate-300">Adicionar infração</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="col-span-1">
            <label className="block text-xs text-slate-400 mb-1">Data da infração</label>
            <input
              type="date"
              value={newData}
              onChange={e => setNewData(e.target.value)}
              max={todayStr()}
              className="h-11 w-full rounded-xl bg-slate-800 border border-slate-700 px-3 text-white text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
          </div>
          <div className="col-span-1">
            <label className="block text-xs text-slate-400 mb-1">Gravidade</label>
            <select
              value={newGravidade}
              onChange={e => setNewGravidade(e.target.value as Gravidade)}
              className="h-11 w-full appearance-none rounded-xl bg-slate-800 border border-slate-700 px-3 text-white text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
            >
              {(Object.entries(GRAVIDADE_LABEL) as [Gravidade, string][]).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1 flex items-end">
            <button
              onClick={addInfracao}
              className="h-11 w-full flex items-center justify-center gap-2 rounded-xl bg-amber-400 text-gray-900 font-semibold px-4 hover:bg-amber-300 active:scale-95 transition-all"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              Adicionar
            </button>
          </div>
        </div>
      </div>

      {/* Lista de infrações */}
      {state.infracoes.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-300">
              Infrações cadastradas
            </p>
            <span className="text-xs text-slate-500">
              {infracoesAtivas.length} no ciclo ·{' '}
              {state.infracoes.length - infracoesAtivas.length} fora
            </span>
          </div>

          <motion.div
            variants={staggerFast}
            initial="hidden"
            animate="visible"
            className="space-y-2"
          >
            <AnimatePresence>
              {infracoesOrdenadas.map(inf => {
                const isActive =
                  new Date(inf.data + 'T00:00:00') >= cutoff;
                return (
                  <motion.div
                    key={inf.id}
                    layout
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: isActive ? 1 : 0.4, y: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.25 }}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 ${GRAVIDADE_STYLE[inf.gravidade]}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm text-white font-medium tabular-nums">
                        {fmtBR(inf.data)}
                      </span>
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${GRAVIDADE_BADGE[inf.gravidade]}`}
                      >
                        {GRAVIDADE_LABEL[inf.gravidade]}
                      </span>
                      {!isActive && (
                        <span className="text-xs text-slate-500 italic">
                          fora do ciclo de 12 meses
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeInfracao(inf.id)}
                      className="text-slate-500 hover:text-red-400 transition-colors ml-3 shrink-0"
                      title="Remover infração"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      )}

      {state.infracoes.length === 0 && (
        <p className="text-xs text-slate-500 text-center">
          Nenhuma infração cadastrada. Adicione acima para simular sua pontuação.
        </p>
      )}
    </motion.div>
  );
};

export default SimuladorCNH;

